import { Box, SimpleGrid, Stack, Group, Badge, Progress, Avatar, Table } from '@mantine/core';
import {
  IconCurrencyDollar,
  IconBriefcase,
  IconTrophy,
  IconPercentage,
  IconArrowRight,
  IconTrendingUp,
  IconTrendingDown,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { crmStats, mockDeals, mockContacts, mockActivities, pipelineStages } from '../mockData/crm';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const StatCard = ({
  title,
  value,
  change,
  icon: Icon,
  color
}: {
  title: string;
  value: string;
  change?: number;
  icon: React.ElementType;
  color: string;
}) => (
  <Card>
    <Group justify="space-between" mb="xs">
      <Text size="sm" c="dimmed">{title}</Text>
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: `var(--mantine-color-${color}-light)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
      </Box>
    </Group>
    <Text size="xl" fw={700}>{value}</Text>
    {change !== undefined && (
      <Group gap="xs" mt="xs">
        {change >= 0 ? (
          <IconTrendingUp size={16} color="var(--mantine-color-green-filled)" />
        ) : (
          <IconTrendingDown size={16} color="var(--mantine-color-red-filled)" />
        )}
        <Text size="xs" c={change >= 0 ? 'green' : 'red'}>
          {change >= 0 ? '+' : ''}{change}%
        </Text>
        <Text size="xs" c="dimmed">vs last month</Text>
      </Group>
    )}
  </Card>
);

export default function CRMDashboardPage() {
  const navigate = useNavigate();
  const recentDeals = mockDeals.slice(0, 5);
  const recentActivities = mockActivities.slice(0, 5);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>CRM Dashboard</Title>
          <Text c="dimmed" size="sm">Manage your sales pipeline and customer relationships</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('contacts')}>View Contacts</Button>
          <Button onClick={() => navigate('pipeline')}>View Pipeline</Button>
        </Group>
      </Group>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Pipeline"
          value={formatCurrency(crmStats.totalPipeline)}
          change={crmStats.pipelineChange}
          icon={IconCurrencyDollar}
          color="green"
        />
        <StatCard
          title="Open Deals"
          value={crmStats.openDeals.toString()}
          icon={IconBriefcase}
          color="blue"
        />
        <StatCard
          title="Won This Month"
          value={formatCurrency(crmStats.wonValue)}
          icon={IconTrophy}
          color="violet"
        />
        <StatCard
          title="Win Rate"
          value={`${crmStats.winRate}%`}
          icon={IconPercentage}
          color="orange"
        />
      </SimpleGrid>

      {/* Pipeline Overview */}
      <Card>
        <Group justify="space-between" mb="md">
          <Text fw={600}>Pipeline Overview</Text>
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate('pipeline')}
          >
            View Pipeline
          </Button>
        </Group>
        <Stack gap="md">
          {pipelineStages.map(stage => (
            <Group key={stage.stage} justify="space-between">
              <Group gap="sm" style={{ flex: 1 }}>
                <Box
                  w={12}
                  h={12}
                  style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${stage.color}-filled)` }}
                />
                <Text size="sm">{stage.stage}</Text>
              </Group>
              <Group gap="sm" style={{ flex: 1 }}>
                <Progress
                  value={(stage.value / crmStats.totalPipeline) * 100}
                  size="sm"
                  radius="xl"
                  style={{ flex: 1 }}
                  color={stage.color}
                />
              </Group>
              <Group gap="sm" w={120} justify="flex-end">
                <Badge size="sm" variant="light">{stage.count} deals</Badge>
                <Text size="sm" fw={500}>{formatCurrency(stage.value)}</Text>
              </Group>
            </Group>
          ))}
        </Stack>
      </Card>

      {/* Recent Deals and Activities */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Recent Deals */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Recent Deals</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('pipeline')}
            >
              View All
            </Button>
          </Group>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Deal</Table.Th>
                <Table.Th>Stage</Table.Th>
                <Table.Th ta="right">Value</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recentDeals.map(deal => (
                <Table.Tr key={deal.id}>
                  <Table.Td>
                    <Box>
                      <Text size="sm" fw={500}>{deal.title}</Text>
                      <Text size="xs" c="dimmed">{deal.company}</Text>
                    </Box>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={
                      deal.stage === 'closed-won' ? 'green' :
                      deal.stage === 'closed-lost' ? 'red' :
                      deal.stage === 'negotiation' ? 'orange' :
                      deal.stage === 'proposal' ? 'cyan' : 'blue'
                    }>
                      {deal.stage.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>{formatCurrency(deal.value)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>

        {/* Recent Activities */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Recent Activities</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('activities')}
            >
              View All
            </Button>
          </Group>
          <Stack gap="sm">
            {recentActivities.map(activity => (
              <Group key={activity.id} gap="sm" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                <Avatar size="sm" radius="xl" color={
                  activity.type === 'call' ? 'green' :
                  activity.type === 'email' ? 'blue' :
                  activity.type === 'meeting' ? 'violet' : 'gray'
                }>
                  {activity.type.charAt(0).toUpperCase()}
                </Avatar>
                <Box style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>{activity.title}</Text>
                  <Text size="xs" c="dimmed">{activity.contactName}</Text>
                </Box>
                <Badge size="xs" variant="light">{activity.type}</Badge>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
